import { useState, useMemo } from "react";
import { SearchInput } from "./SearchInput";
import { ClientTableHeader } from "./clients/ClientTableHeader";
import { ClientTableRow } from "./clients/ClientTableRow";

interface Client {
  id: number;
  name: string;
  status: string;
  source: string;
  last_contact: string;
  email?: string;
}

interface AdminClientsTableProps {
  clients?: Client[];
  isLoading: boolean;
  onViewDetails: (clientId: number) => void;
  onClientDeleted?: () => void;
}

export const AdminClientsTable = ({ 
  clients, 
  isLoading,
  onViewDetails,
  onClientDeleted 
}: AdminClientsTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  console.log('AdminClientsTable - Received clients:', clients);

  const filteredClients = useMemo(() => {
    if (!clients) {
      console.log('No clients data available');
      return [];
    }
    if (!searchQuery.trim()) {
      console.log('Showing all clients:', clients.length);
      return clients;
    }

    const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/);
    
    return clients.filter(client => {
      const searchableFields = [
        client.name?.toLowerCase() || '',
        client.email?.toLowerCase() || '',
        client.status?.toLowerCase() || '',
        client.source?.toLowerCase() || ''
      ];
      
      return searchTerms.every(term =>
        searchableFields.some(field => field.includes(term))
      );
    });
  }, [clients, searchQuery]);

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading client data...</p>
      </div>
    );
  }

  if (!clients?.length) {
    console.log('No clients found in AdminClientsTable');
    return (
      <div className="rounded-xl bg-[#222222] border border-white/10 backdrop-blur-sm overflow-hidden">
        <div className="p-6">
          <ClientTableHeader />
          <div className="text-center py-8 text-gray-400">
            No clients found. Add your first client to get started.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-[#222222] border border-white/10 backdrop-blur-sm overflow-hidden">
      <div className="p-6">
        <ClientTableHeader />
        <div className="mb-4">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search clients by name, email, status..."
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">Name</th>
                <th className="text-left p-4 text-gray-400 font-medium">Email</th>
                <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                <th className="text-left p-4 text-gray-400 font-medium">Source</th>
                <th className="text-left p-4 text-gray-400 font-medium">Last Contact</th>
                <th className="text-left p-4 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <ClientTableRow key={client.id} client={client} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};